import { ListMagnifyingGlassIcon } from '@phosphor-icons/react';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/common/button';

import { DEVICE_COPY, DEVICE_POLICY, type CommandType } from '../constants';
import type { Device } from '../types';
import { useDeviceFieldLinks } from '../use-linked-field';

import { DeviceCard } from './device-card';
import { DeviceCardSkeleton } from './device-card-skeleton';
import styles from './device-grid.module.scss';

export type DeviceGridProps = {
  devices: Device[];
  isLoading: boolean;
  /** Set when the *first* batch failed — the whole grid is replaced by the error state. */
  error: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** Set when a *subsequent* batch failed — loaded devices stay, with an inline retry below. */
  nextPageError: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  onQueueCommand: (device: Device, command: CommandType) => void;
};

/**
 * The device grid with infinite scroll (US-112439 §12).
 *
 * **This component is the scroll container**, not the page: the title, category tabs and search
 * row stay fixed while only the cards scroll. That also makes the IntersectionObserver honest —
 * its `root` is this element, so the 200px lead is measured against the list's own viewport
 * rather than the browser's.
 *
 * Batches load progressively as the sentinel below the grid comes into view — no pagination
 * controls. A failed batch keeps the already-loaded devices on screen and offers an inline Retry
 * that re-requests the *same* batch, so no partial or duplicate devices are inserted.
 */
export function DeviceGrid({
  devices,
  isLoading,
  error,
  hasNextPage,
  isFetchingNextPage,
  nextPageError,
  onLoadMore,
  onRetry,
  onQueueCommand,
}: DeviceGridProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  // One join for the whole page rather than one per card: the two queries behind it are the Map
  // tab's, so this is usually cache-warm and costs no request. See `useDeviceFieldLinks`.
  const { links } = useDeviceFieldLinks();
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    const root = scrollerRef.current;
    // Don't observe while a batch is in flight or after a failure — otherwise scrolling past a
    // failed sentinel would retry in a loop instead of waiting for the explicit Retry.
    if (!node || !root || !hasNextPage || isFetchingNextPage || nextPageError) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      { root, rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, nextPageError, onLoadMore]);

  function content() {
    if (isLoading) {
      return (
        <div className={styles.grid} aria-busy="true" aria-label="Loading devices">
          {Array.from({ length: DEVICE_POLICY.PAGE_SIZE }, (_, index) => (
            <DeviceCardSkeleton key={index} />
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.state} role="alert">
          <p className={styles.stateText}>{DEVICE_COPY.BATCH_ERROR}</p>
          <Button variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        </div>
      );
    }

    // The same empty state serves "no devices linked to my account" and "search/filter matched
    // nothing" (US-112439 §9, §15) — the AC gives both the identical message.
    if (devices.length === 0) {
      return (
        <div className={styles.state}>
          <ListMagnifyingGlassIcon size={20} aria-hidden className={styles.emptyIcon} />
          <p className={styles.stateText}>{DEVICE_COPY.EMPTY}</p>
        </div>
      );
    }

    return (
      <>
        <div className={styles.grid} data-testid="device-grid">
          {devices.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              linkedFieldName={links.get(device.id)?.name ?? null}
              onQueueCommand={onQueueCommand}
            />
          ))}
        </div>

        {nextPageError ? (
          <div className={styles.batchError} role="alert">
            <p className={styles.stateText}>{DEVICE_COPY.BATCH_ERROR}</p>
            <Button variant="secondary" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : null}

        {isFetchingNextPage ? (
          <p className={styles.loadingMore} role="status">
            Loading more devices…
          </p>
        ) : null}

        {hasNextPage ? <div ref={sentinelRef} className={styles.sentinel} aria-hidden /> : null}
      </>
    );
  }

  return (
    <div className={styles.scroller} ref={scrollerRef} data-testid="device-scroller">
      {content()}
    </div>
  );
}
